import { useState, useEffect } from 'react'
import {
  Languages,
  Settings,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Sparkles,
  Eye,
  EyeOff,
  Info,
} from 'lucide-react'
import {
  getTranslateConfig,
  updateTranslateConfig,
  type TranslateConfig,
} from '@/services/api'
import {
  AI_PROVIDERS,
  PROVIDER_STYLES,
  REASONING_STYLES,
  getProviderFromModel,
  getProviderInfo,
  getReasoningLevelsForProvider,
  getDefaultReasoningForProvider,
} from '@/config/models'

const LANGUAGES = [
  { id: 'en', name: 'English' },
  { id: 'es', name: 'Spanish' },
  { id: 'no', name: 'Norwegian' },
  { id: 'de', name: 'German' },
  { id: 'fr', name: 'French' },
  { id: 'pt', name: 'Portuguese' },
  { id: 'it', name: 'Italian' },
  { id: 'sv', name: 'Swedish' },
  { id: 'da', name: 'Danish' },
  { id: 'nl', name: 'Dutch' },
  { id: 'fi', name: 'Finnish' },
  { id: 'pl', name: 'Polish' },
  { id: 'ja', name: 'Japanese' },
  { id: 'ko', name: 'Korean' },
  { id: 'zh', name: 'Chinese' },
  { id: 'ar', name: 'Arabic' },
]

const DEFAULT_TRANSLATE_PROMPT = `You are a professional translator for customer support.
Detect the language of the text and translate it to {target_language}.
If a source language is specified ({source_language}), use it instead of auto-detection.
Maintain the tone, formatting, and meaning of the original message. Do not add explanations or notes.
Output ONLY the translated text.`

// Module-level cache
let _cachedTranslateConfig: TranslateConfig | null = null

export default function AITranslatorPage() {
  const [config, setConfig] = useState<TranslateConfig>(_cachedTranslateConfig || {
    enabled: false,
    model: 'gpt-5-mini',
    reasoning_effort: 'low',
    target_language: 'en',
    system_prompt: undefined,
  })
  const [loading, setLoading] = useState(!_cachedTranslateConfig)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<TranslateConfig>({ ...config })
  const [showPromptPreview, setShowPromptPreview] = useState(false)
  const [defaultPrompt, setDefaultPrompt] = useState(DEFAULT_TRANSLATE_PROMPT)

  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    try {
      if (!_cachedTranslateConfig) setLoading(true)
      setError(null)
      const data = await getTranslateConfig()
      if (data.default_prompt) {
        setDefaultPrompt(data.default_prompt)
      }
      const configData: TranslateConfig = {
        enabled: data.enabled,
        model: data.model,
        reasoning_effort: data.reasoning_effort,
        target_language: data.target_language || 'en',
        system_prompt: data.system_prompt,
      }
      setConfig(configData)
      setFormData(configData)
      _cachedTranslateConfig = configData
    } catch (err: any) {
      setError(err.message || 'Failed to load translator configuration')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      await updateTranslateConfig(formData)
      setConfig(formData)
      setIsEditing(false)
    } catch (err: any) {
      setError(err.message || 'Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData({ ...config })
    setIsEditing(false)
    setError(null)
  }

  if (loading && !_cachedTranslateConfig) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Languages className="w-8 h-8 mr-3 text-teal-500" />
            AI Translator
          </h1>
          <p className="text-gray-600 mt-2">
            Translate replies and messages to communicate with customers in their language
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-600">{error}</span>
        </div>
      )}

      {/* Configuration Card */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Settings className="w-5 h-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Translator Configuration</h2>
          </div>
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Configure</span>
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-400 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {/* Enabled Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${formData.enabled ? 'bg-teal-100' : 'bg-gray-200'}`}>
                <Languages className={`w-5 h-5 ${formData.enabled ? 'text-teal-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <p className="font-medium text-gray-900">AI Translator</p>
                <p className="text-sm text-gray-500">
                  {formData.enabled
                    ? 'Translation is available for replies and thread messages'
                    : 'Translation is disabled — manual translation only'}
                </p>
              </div>
            </div>
            {isEditing ? (
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
              </label>
            ) : (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                formData.enabled ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-700'
              }`}>
                {formData.enabled ? 'Enabled' : 'Disabled'}
              </span>
            )}
          </div>

          {/* Target Language */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Target Language
            </label>
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
              {LANGUAGES.map((lang) => {
                const isSelected = formData.target_language === lang.id
                return (
                  <button
                    key={lang.id}
                    type="button"
                    disabled={!isEditing}
                    onClick={() => {
                      if (isEditing) {
                        setFormData({ ...formData, target_language: lang.id })
                      }
                    }}
                    className={`px-2 py-2 rounded-lg border-2 text-center transition-all text-xs ${
                      isSelected
                        ? 'border-teal-500 bg-teal-50 text-teal-700 font-semibold shadow-sm'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-teal-50 hover:border-teal-200'
                    } ${!isEditing ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                  >
                    {lang.name}
                  </button>
                )
              })}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              The default language for translations. Can be overridden per-message.
            </p>
          </div>

          {/* AI Provider Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              AI Provider
            </label>
            <div className="grid grid-cols-4 gap-2">
              {AI_PROVIDERS.map((provider) => {
                const styles = PROVIDER_STYLES[provider.id]
                const isSelected = getProviderFromModel(formData.model) === provider.id
                return (
                  <button
                    key={provider.id}
                    type="button"
                    disabled={!isEditing}
                    onClick={() => {
                      if (isEditing) {
                        const defaultModel = provider.models.find(m => m.recommended)?.id || provider.models[0].id
                        const newReasoning = getDefaultReasoningForProvider(provider.id, formData.reasoning_effort)
                        setFormData({ ...formData, model: defaultModel, reasoning_effort: newReasoning })
                      }
                    }}
                    className={`relative px-3 py-2.5 rounded-lg border-2 text-center transition-all ${
                      isSelected
                        ? styles.pillActive + ' ' + styles.pillActiveText
                        : styles.pill + ' ' + styles.pillText
                    } ${!isEditing ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${isSelected ? styles.dot : 'bg-gray-300'}`} />
                      <span className="text-sm">{provider.name}</span>
                    </div>
                    {!provider.available && (
                      <span className="block text-[10px] mt-0.5 opacity-60">Coming Soon</span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Model Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Model
            </label>
            {(() => {
              const currentProvider = getProviderInfo(getProviderFromModel(formData.model))
              const styles = PROVIDER_STYLES[currentProvider?.id || 'openai']
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {currentProvider?.models.map((model) => {
                    const isSelected = formData.model === model.id
                    return (
                      <button
                        key={model.id}
                        type="button"
                        disabled={!isEditing}
                        onClick={() => {
                          if (isEditing) {
                            setFormData({ ...formData, model: model.id })
                          }
                        }}
                        className={`relative px-3 py-3 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? styles.ring + ' bg-gray-50 shadow-sm'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        } ${!isEditing ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className={`text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                              {model.name}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">{model.description}</p>
                          </div>
                          {model.recommended && (
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${styles.badge}`}>
                              Top
                            </span>
                          )}
                        </div>
                        {isSelected && (
                          <div className={`absolute top-1.5 right-1.5 w-2 h-2 rounded-full ${styles.dot}`} />
                        )}
                      </button>
                    )
                  })}
                </div>
              )
            })()}
            {!getProviderInfo(getProviderFromModel(formData.model))?.available && (
              <p className="mt-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                This provider is not yet available. Selection will be saved for when it becomes active.
              </p>
            )}
          </div>

          {/* Reasoning Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Reasoning Level
            </label>
            {(() => {
              const levels = getReasoningLevelsForProvider(getProviderFromModel(formData.model))
              return (
                <div className={`grid gap-2 ${levels.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                  {levels.map((level) => {
                    const isSelected = formData.reasoning_effort === level.id
                    const styles = REASONING_STYLES[level.id]
                    return (
                      <button
                        key={level.id}
                        type="button"
                        disabled={!isEditing}
                        onClick={() => {
                          if (isEditing) {
                            setFormData({ ...formData, reasoning_effort: level.id })
                          }
                        }}
                        className={`px-3 py-3 rounded-lg border-2 text-center transition-all ${
                          isSelected
                            ? styles.cardActive + ' ' + styles.textActive
                            : styles.card + ' ' + styles.text
                        } ${!isEditing ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                      >
                        <p className="text-sm font-semibold">{level.name}</p>
                        <p className="text-[11px] mt-0.5 opacity-75">{level.description}</p>
                      </button>
                    )
                  })}
                </div>
              )
            })()}
            <p className="mt-2 text-xs text-gray-500">
              Higher reasoning provides better accuracy but increases response time and cost.
            </p>
          </div>

          {/* Custom System Prompt */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Translation Prompt
              </label>
              <button
                onClick={() => setShowPromptPreview(!showPromptPreview)}
                className="text-xs text-teal-600 hover:text-teal-700 flex items-center space-x-1"
              >
                {showPromptPreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                <span>{showPromptPreview ? 'Hide' : 'Show'} {formData.system_prompt ? 'Current' : 'Default'} Prompt</span>
              </button>
            </div>

            {/* Status indicator */}
            <div className={`mb-3 p-3 rounded-lg border ${
              formData.system_prompt
                ? 'bg-purple-50 border-purple-200'
                : 'bg-teal-50 border-teal-200'
            }`}>
              <div className="flex items-start space-x-2">
                {formData.system_prompt ? (
                  <>
                    <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-purple-900">Using Custom Prompt</p>
                      <p className="text-xs text-purple-700 mt-0.5">
                        Your custom rules define how translations are performed.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-teal-900">Using Default Prompt</p>
                      <p className="text-xs text-teal-700 mt-0.5">
                        The translator uses built-in rules optimized for customer support translations.
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {showPromptPreview && (
              <div className="mb-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-900">
                    {formData.system_prompt ? 'Current Prompt (Custom)' : 'Default Prompt (Active)'}
                  </p>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    formData.system_prompt
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {formData.system_prompt ? 'Custom' : 'Default'}
                  </span>
                </div>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap bg-white p-3 rounded border border-gray-200 max-h-64 overflow-y-auto">
                  {formData.system_prompt || defaultPrompt}
                </pre>
              </div>
            )}

            <textarea
              value={formData.system_prompt || ''}
              onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value || undefined })}
              disabled={!isEditing}
              placeholder="Leave empty to use the default prompt, or enter your own custom translation rules..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-50 disabled:text-gray-500"
              rows={6}
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional: Override the default prompt with your own translation instructions. Use {'{target_language}'} for the target language and {'{source_language}'} for the source language.
            </p>
          </div>
        </div>
      </div>

      {/* How It Works Card */}
      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl border border-teal-100 p-6">
        <div className="flex items-start space-x-4">
          <div className="p-3 bg-teal-100 rounded-lg">
            <Info className="w-6 h-6 text-teal-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-2">How AI Translator Works</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                <strong>1.</strong> Enable the translator and select your target language and preferred AI model.
              </p>
              <p>
                <strong>2.</strong> When composing a reply, click the translate button to translate your message before sending.
              </p>
              <p>
                <strong>3.</strong> For incoming messages, click the translate icon on any message in the thread to see a translation.
              </p>
              <p>
                <strong>4.</strong> Translations use the selected AI model to maintain tone, context, and formatting.
              </p>
            </div>
            <div className="mt-4 p-3 bg-white rounded-lg border border-teal-100">
              <p className="text-xs font-medium text-gray-700 mb-2">Supported Use Cases</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-teal-500"></span>
                  <span className="text-xs text-gray-600"><strong>Reply Translation</strong> — Translate your draft reply</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-cyan-500"></span>
                  <span className="text-xs text-gray-600"><strong>Message Translation</strong> — Translate any thread message</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {!isEditing && !error && !loading && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-sm text-green-600">Translator configuration is saved and active</span>
        </div>
      )}
    </div>
  )
}
